/**
 * Finances — 3 onglets : Dons individuels / Quêtes de culte / Rapports.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from "recharts";
import { colors, fonts, goldGradient } from "../styles/tokens";
import useAuth from "../hooks/useAuth";
import useMembers from "../hooks/useMembers";
import useToast from "../hooks/useToast";
import { donsApi, quetesApi } from "../services/api";
import Card from "../components/Card";
import Badge from "../components/Badge";
import Button from "../components/Button";
import DonForm from "../components/DonForm";
import QueteForm from "../components/QueteForm";

// ─── Configuration UI ───
const TYPE_VARIANT = { dime: "gold", offrande: "success", projet: "info", mission: "warning", autre: "default" };
const TYPE_LABELS  = { dime: "Dîme", offrande: "Offrande", projet: "Don spécial", mission: "Mission", autre: "Autre" };

const DONATEUR_CONFIG = {
  membre:       { icon: "👤", label: "Membre",       color: "#60A5FA" },
  anonyme:      { icon: "🎭", label: "Anonyme",      color: "#8A97B0" },
  organisation: { icon: "🏛", label: "Organisation", color: "#A78BFA" },
};

const DEVISE_SYMBOLS = { EUR: "€", USD: "$", XOF: "F CFA", XAF: "F CFA", CAD: "$", CHF: "CHF" };

const TABS = [
  { id: "dons",     label: "Dons individuels", icon: "💝" },
  { id: "quetes",   label: "Quêtes de culte",  icon: "⛪" },
  { id: "rapports", label: "Rapports",         icon: "📊" },
];

const fmtMoney = (v, devise = "EUR") => {
  const sym = DEVISE_SYMBOLS[devise] || devise;
  return `${(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} ${sym}`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
};

export default function Finances() {
  const { isAdmin } = useAuth();
  const { members, eglises } = useMembers();
  const toast = useToast();

  const [active, setActive] = useState("dons");

  // ─── Dons ───
  const [dons, setDons] = useState([]);
  const [donsLoading, setDonsLoading] = useState(true);
  const [donFormOpen, setDonFormOpen] = useState(false);

  // ─── Quêtes ───
  const [quetes, setQuetes] = useState([]);
  const [quetesLoading, setQuetesLoading] = useState(true);
  const [queteStats, setQueteStats] = useState(null);
  const [queteFormOpen, setQueteFormOpen] = useState(false);
  const [editingQuete, setEditingQuete] = useState(null);

  // ─── Loaders ───
  const loadDons = useCallback(async () => {
    setDonsLoading(true);
    try {
      const { data } = await donsApi.list();
      setDons(data.items || []);
    } catch (err) {
      const isNetwork = !err.response || err.code === "ERR_NETWORK";
      if (!isNetwork) toast.error(err.response?.data?.detail || "Erreur de chargement (dons)");
      setDons([]);
    } finally {
      setDonsLoading(false);
    }
  }, [toast]);

  const loadQuetes = useCallback(async () => {
    if (!isAdmin) return;
    setQuetesLoading(true);
    try {
      const [{ data: list }, { data: stats }] = await Promise.all([
        quetesApi.list(),
        quetesApi.stats(),
      ]);
      setQuetes(list.items || []);
      setQueteStats(stats);
    } catch (err) {
      const isNetwork = !err.response || err.code === "ERR_NETWORK";
      if (!isNetwork) toast.error(err.response?.data?.detail || "Erreur de chargement (quêtes)");
      setQuetes([]);
      setQueteStats(null);
    } finally {
      setQuetesLoading(false);
    }
  }, [isAdmin, toast]);

  useEffect(() => { loadDons(); }, [loadDons]);
  useEffect(() => { loadQuetes(); }, [loadQuetes]);

  // ─── Handlers ───
  const handleCreateDon = async (payload) => {
    try {
      await donsApi.create(payload);
      toast.success("✓ Don enregistré avec succès");
      setDonFormOpen(false);
      await loadDons();
      return { ok: true };
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = "Erreur lors de l'enregistrement";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail[0]?.msg) msg = detail[0].msg.replace(/^Value error,\s*/, "");
      toast.error(msg);
      return { ok: false, error: msg };
    }
  };

  const handleSubmitQuete = async (payload, { total }) => {
    try {
      if (editingQuete) {
        await quetesApi.update(editingQuete.id, payload);
        toast.success(`Quête mise à jour — Total : ${fmtMoney(total, payload.devise)}`);
      } else {
        await quetesApi.create(payload);
        toast.success(`✓ Quête enregistrée — Total : ${fmtMoney(total, payload.devise)}`);
      }
      setQueteFormOpen(false);
      setEditingQuete(null);
      await loadQuetes();
      return { ok: true };
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = "Erreur lors de l'enregistrement";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail[0]?.msg) msg = detail[0].msg.replace(/^Value error,\s*/, "");
      toast.error(msg);
      return { ok: false, error: msg };
    }
  };

  const handleDeleteQuete = async (quete) => {
    try {
      await quetesApi.remove(quete.id);
      toast.success("Quête supprimée");
      setQueteFormOpen(false);
      setEditingQuete(null);
      await loadQuetes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de suppression");
    }
  };

  // ─── Stats dérivées ───
  const donsStats = useMemo(() => {
    const total = dons.reduce((s, d) => s + (d.montant || 0), 0);
    const totalDime     = dons.filter((d) => d.type_don === "dime")    .reduce((s, d) => s + d.montant, 0);
    const totalOffrande = dons.filter((d) => d.type_don === "offrande").reduce((s, d) => s + d.montant, 0);
    return { total, totalDime, totalOffrande, count: dons.length };
  }, [dons]);

  const repartition = useMemo(() => {
    const totals = { membre: 0, anonyme: 0, organisation: 0 };
    const counts = { membre: 0, anonyme: 0, organisation: 0 };
    dons.forEach((d) => {
      const t = d.type_donateur || "membre";
      totals[t] = (totals[t] || 0) + (d.montant || 0);
      counts[t] = (counts[t] || 0) + 1;
    });
    const grand = totals.membre + totals.anonyme + totals.organisation;
    return ["membre", "anonyme", "organisation"]
      .map((t) => ({
        type: t, ...DONATEUR_CONFIG[t],
        total: totals[t], count: counts[t],
        pct: grand > 0 ? (totals[t] / grand) * 100 : 0,
      }))
      .filter((r) => r.count > 0);
  }, [dons]);

  return (
    <div style={{ fontFamily: fonts.body, color: colors.text }}>
      <header style={{ marginBottom: "20px", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" }}>
        <div>
          <h1 style={{ fontFamily: fonts.heading, fontSize: "36px", fontWeight: 700, marginBottom: "4px" }}>
            {isAdmin ? "Finances" : "Mes dons"}
          </h1>
          <p style={{ color: colors.textMuted, fontSize: "14px" }}>
            {isAdmin ? "Suivi complet des contributions" : "Historique de vos contributions"}
          </p>
        </div>
        {isAdmin && active === "dons"   && <Button onClick={() => setDonFormOpen(true)}>+ Enregistrer un don</Button>}
        {isAdmin && active === "quetes" && <Button onClick={() => { setEditingQuete(null); setQueteFormOpen(true); }}>+ Nouvelle quête</Button>}
      </header>

      {/* Onglets (admin uniquement) */}
      {isAdmin && (
        <div style={{
          display: "flex", gap: "4px", flexWrap: "wrap",
          background: colors.card, padding: "6px", borderRadius: "12px",
          border: `1px solid ${colors.border}`,
          marginBottom: "20px",
        }}>
          {TABS.map((t) => {
            const isActive = active === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                style={{
                  flex: "1 1 auto",
                  minWidth: 130,
                  padding: "10px 16px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? colors.bg : colors.text,
                  background: isActive ? goldGradient : "transparent",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <span>{t.icon}</span>
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* TAB DONS */}
      {(active === "dons" || !isAdmin) && (
        <DonsTab
          dons={dons}
          loading={donsLoading}
          isAdmin={isAdmin}
          stats={donsStats}
        />
      )}

      {/* TAB QUÊTES */}
      {active === "quetes" && isAdmin && (
        <QuetesTab
          quetes={quetes}
          loading={quetesLoading}
          stats={queteStats}
          onCreate={() => { setEditingQuete(null); setQueteFormOpen(true); }}
          onEdit={(q) => { setEditingQuete(q); setQueteFormOpen(true); }}
        />
      )}

      {/* TAB RAPPORTS */}
      {active === "rapports" && isAdmin && (
        <RapportsTab
          repartition={repartition}
          queteStats={queteStats}
          donsStats={donsStats}
        />
      )}

      {/* Modales */}
      <DonForm
        open={donFormOpen}
        onClose={() => setDonFormOpen(false)}
        onSubmit={handleCreateDon}
        members={members}
        eglises={eglises}
      />
      <QueteForm
        open={queteFormOpen}
        onClose={() => { setQueteFormOpen(false); setEditingQuete(null); }}
        onSubmit={handleSubmitQuete}
        onDelete={handleDeleteQuete}
        eglises={eglises}
        initial={editingQuete}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TAB 1 — DONS INDIVIDUELS
// ════════════════════════════════════════════════════════════

function DonsTab({ dons, loading, isAdmin, stats }) {
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginBottom: "20px" }}>
        <KpiBox label="Total"          value={fmtMoney(stats.total)}        icon="💰" variant="gold" />
        <KpiBox label="Dîmes"          value={fmtMoney(stats.totalDime)}    icon="📖" />
        <KpiBox label="Offrandes"      value={fmtMoney(stats.totalOffrande)} icon="🎁" />
        <KpiBox label="Nombre de dons" value={stats.count}                  icon="📊" />
      </div>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            <thead>
              <tr style={{ background: colors.surface, borderBottom: `1px solid ${colors.border}` }}>
                {(isAdmin
                  ? ["Date", "Donateur", "Type", "Méthode", "Note", "Montant"]
                  : ["Date", "Type", "Méthode", "Note", "Montant"]
                ).map((h) => (
                  <th key={h} style={th(h === "Montant")}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={isAdmin ? 6 : 5} style={emptyCell}>Chargement…</td></tr>
              ) : dons.length === 0 ? (
                <tr><td colSpan={isAdmin ? 6 : 5} style={emptyCell}>Aucun don enregistré</td></tr>
              ) : (
                dons.map((d) => {
                  const cfg = DONATEUR_CONFIG[d.type_donateur] || DONATEUR_CONFIG.membre;
                  return (
                    <tr key={d.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <td style={td}>{fmtDate(d.date_don)}</td>
                      {isAdmin && <td style={td}><DonateurCell don={d} cfg={cfg} /></td>}
                      <td style={td}><Badge variant={TYPE_VARIANT[d.type_don]}>{TYPE_LABELS[d.type_don] || d.type_don}</Badge></td>
                      <td style={{ ...td, color: colors.textMuted, textTransform: "capitalize" }}>{(d.methode || "").replace(/_/g, " ")}</td>
                      <td style={{ ...td, color: colors.textMuted, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>{d.note || "—"}</td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 700, color: colors.gold, fontSize: "15px" }}>
                        {fmtMoney(d.montant, d.devise)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

// ════════════════════════════════════════════════════════════
// TAB 2 — QUÊTES DE CULTE
// ════════════════════════════════════════════════════════════

function QuetesTab({ quetes, loading, stats, onCreate, onEdit }) {
  if (loading) {
    return <div style={{ color: colors.textMuted }}>Chargement des quêtes...</div>;
  }

  return (
    <>
      {/* KPIs Quêtes */}
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginBottom: "20px" }}>
          <KpiBox label="Total ce mois"      value={fmtMoney(stats.total_mois)}       icon="📅" variant="gold" />
          <KpiBox label="Total cette année"  value={fmtMoney(stats.total_annee)}      icon="📊" />
          <KpiBox label="Moyenne par culte"  value={fmtMoney(stats.moyenne_par_culte)} icon="⛪" />
          <KpiBox label="Meilleure quête"    value={fmtMoney(stats.meilleure_quete)}   icon="🏆" />
        </div>
      )}

      {/* Liste */}
      {quetes.length === 0 ? (
        <Card style={{ textAlign: "center", padding: "60px" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>⛪</div>
          <h3 style={{ fontFamily: fonts.heading, fontSize: "22px" }}>Aucune quête enregistrée</h3>
          <p style={{ color: colors.textMuted, marginTop: "8px" }}>
            Saisissez la première recette de culte de votre église.
          </p>
          <div style={{ marginTop: "16px" }}>
            <Button onClick={onCreate}>+ Nouvelle quête</Button>
          </div>
        </Card>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))", gap: "16px" }}>
          {quetes.map((q) => (
            <QueteCard key={q.id} quete={q} onEdit={() => onEdit(q)} />
          ))}
        </div>
      )}
    </>
  );
}

function QueteCard({ quete, onEdit }) {
  const date = new Date(quete.date_culte).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" });
  return (
    <Card hover>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
          <div style={{
            width: 44, height: 44, borderRadius: "10px",
            background: colors.goldDim, color: colors.gold,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: "20px", flexShrink: 0,
          }}>⛪</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: fonts.heading, fontSize: "18px", fontWeight: 600, textTransform: "capitalize" }}>
              Culte du {date}
            </div>
            <div style={{ color: colors.textMuted, fontSize: "12px", marginTop: "2px" }}>
              {quete.eglise_nom}
              {quete.evenement_titre && ` · ${quete.evenement_titre}`}
            </div>
          </div>
        </div>

        {/* Lignes */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px", paddingTop: "8px", borderTop: `1px solid ${colors.border}` }}>
          {quete.lignes.map((l) => (
            <div key={l.id} style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
              <span style={{ color: colors.textMuted }}>{l.libelle}</span>
              <span style={{ color: colors.text, fontVariantNumeric: "tabular-nums" }}>
                {fmtMoney(l.montant, quete.devise)}
              </span>
            </div>
          ))}
        </div>

        {/* Total */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "baseline",
          paddingTop: "10px", borderTop: `1px solid ${colors.border}`,
        }}>
          <span style={{ fontSize: "12px", letterSpacing: "0.05em", textTransform: "uppercase", color: colors.textMuted, fontWeight: 600 }}>
            Total
          </span>
          <span style={{ fontFamily: fonts.heading, fontSize: "22px", fontWeight: 700, color: colors.gold }}>
            {fmtMoney(quete.total, quete.devise)}
          </span>
        </div>

        {quete.compte_par && (
          <div style={{ fontSize: "12px", color: colors.textMuted }}>
            👥 Compté par : {quete.compte_par}
          </div>
        )}

        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", paddingTop: "4px" }}>
          <Button size="sm" variant="ghost" onClick={onEdit}>Modifier</Button>
        </div>
      </div>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════
// TAB 3 — RAPPORTS
// ════════════════════════════════════════════════════════════

function RapportsTab({ repartition, queteStats, donsStats }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Synthèse globale */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px" }}>
        <KpiBox label="Total dons individuels" value={fmtMoney(donsStats.total)}            icon="💝" />
        <KpiBox label="Total quêtes (mois)"    value={fmtMoney(queteStats?.total_mois || 0)} icon="⛪" variant="gold" />
        <KpiBox label="Total quêtes (année)"   value={fmtMoney(queteStats?.total_annee || 0)} icon="📊" />
        <KpiBox label="Moyenne par culte"      value={fmtMoney(queteStats?.moyenne_par_culte || 0)} icon="🏆" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 1fr) 2fr", gap: "16px" }} className="rapports-grid">
        {/* Répartition donateurs */}
        {repartition.length > 0 && (
          <Card>
            <h3 style={{ fontFamily: fonts.heading, fontSize: "20px", marginBottom: "16px" }}>
              Répartition des donateurs
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              {repartition.map((r) => (
                <div key={r.type}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "4px" }}>
                    <span style={{ fontSize: "13px", color: colors.text }}>
                      <span style={{ marginRight: "6px" }}>{r.icon}</span>{r.label}
                    </span>
                    <span style={{ fontSize: "13px", color: colors.textMuted }}>
                      <strong style={{ color: colors.text }}>{fmtMoney(r.total)}</strong>
                      &nbsp;<span style={{ color: r.color }}>({r.pct.toFixed(0)}%)</span>
                    </span>
                  </div>
                  <div style={{ height: 8, borderRadius: 999, background: colors.border, overflow: "hidden" }}>
                    <div style={{ width: `${r.pct}%`, height: "100%", background: r.color, transition: "width 400ms" }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Évolution quêtes 12 mois */}
        {queteStats?.evolution_12_mois?.length > 0 && (
          <Card>
            <h3 style={{ fontFamily: fonts.heading, fontSize: "20px", marginBottom: "16px" }}>
              Évolution des quêtes (12 mois)
            </h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={queteStats.evolution_12_mois}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.border} vertical={false} />
                <XAxis dataKey="label" stroke={colors.textMuted} fontSize={12} tickLine={false} />
                <YAxis stroke={colors.textMuted} fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: colors.surface, border: `1px solid ${colors.gold}`, borderRadius: "8px" }}
                  labelStyle={{ color: colors.textMuted }}
                  formatter={(v) => fmtMoney(v, queteStats.devise)}
                />
                <Line type="monotone" dataKey="value" stroke={colors.gold} strokeWidth={3} dot={{ fill: colors.gold, r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>

      <style>{`
        @media (max-width: 900px) {
          .rapports-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

// ─── Composants partagés ───

function DonateurCell({ don, cfg }) {
  const subText =
    don.type_donateur === "organisation" && don.organisation_pays ? don.organisation_pays
    : don.type_donateur === "anonyme" ? "Sans nom"
    : null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 200 }}>
      <span
        style={{
          width: 32, height: 32, borderRadius: "50%",
          background: `${cfg.color}25`,
          color: cfg.color,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "16px",
          flexShrink: 0,
          border: `1px solid ${cfg.color}50`,
        }}
        title={cfg.label}
      >
        {cfg.icon}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: "14px", fontWeight: 500, color: colors.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {don.nom_donateur || "—"}
        </div>
        <div style={{ fontSize: "11px", color: cfg.color, fontWeight: 500 }}>
          {cfg.label}{subText ? ` · ${subText}` : ""}
        </div>
      </div>
    </div>
  );
}

function KpiBox({ label, value, icon, variant = "default" }) {
  const accent = variant === "gold" ? colors.gold : colors.text;
  return (
    <Card hover>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
        <span style={{ fontSize: "13px", color: colors.textMuted }}>{label}</span>
        <span style={{ fontSize: "18px" }}>{icon}</span>
      </div>
      <div style={{ fontFamily: fonts.heading, fontSize: "26px", fontWeight: 700, color: accent }}>
        {value}
      </div>
    </Card>
  );
}

const th = (alignRight) => ({
  padding: "14px 16px",
  textAlign: alignRight ? "right" : "left",
  fontSize: "12px",
  fontWeight: 600,
  color: colors.textMuted,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
});

const td = { padding: "14px 16px", fontSize: "13px", whiteSpace: "nowrap" };

const emptyCell = { padding: "40px", textAlign: "center", color: colors.textMuted };
